import { saveAs } from 'file-saver';

import { 
    Table,
    Packer,
    TextRun,
    TableRow,
    WidthType,
    TableCell,
    BorderStyle,
    UnderlineType,
    AlignmentType,
    ExternalHyperlink,
    Document as DocumentDocx,
    Paragraph as ParagraphDocx
} from "docx";

import getEditorExtensions from "./tiptap-extensions";
import { generateJSON } from '@tiptap/html';
import { BuilderSections, getContentStructureFrom } from '../store/builder-utils';
import { decodeHtmlEntities, rgbToHex, extractContent } from './helpers';

const BPS_LEVEL = {
    '0': '• ',
    '1': '   ◦ ',
    '2': '       ▪ '
}

const extractContentFrom = (htmlMarkup) => {
    if(htmlMarkup == null) {
        return []
    }
    
    const content = [];
    const JSONMarkup = generateJSON(
        htmlMarkup || '',
        getEditorExtensions(true, true, 'true', undefined)
    );
    
    JSONMarkup.content.forEach((item) => {
        if(item?.type === 'paragraph' && Array.isArray(item?.content)){
            content.push(getContentStructureFrom(item));
        }else if(item?.type === 'bulletList' && Array.isArray(item?.content)){
            item?.content?.forEach((listItem) => {
                content.push(getContentStructureFrom(listItem));
            })
        }else{
            content.push(...getContentStructureFrom(item));
        }
    })
    
    return content;
}

const createHeading = (text) => {
    return new ParagraphDocx({
        style: "heading",
        children: textFormatting(text)
    })
}

const createSubheading = (text, params = {}) => {
    return new ParagraphDocx({...{
        alignment: AlignmentType.CENTER,
        style: "subheading",
        children: textFormatting(text)
    }, ...params})
}

const createMessage = (text, params = {}) => {
    return new ParagraphDocx({...{
        alignment: AlignmentType.LEFT,
        style: "subheading",
        children: textFormatting(text)
    }, ...params})  
}

const createSectionHeading = (title, {before = 20}) => {
    return new ParagraphDocx({
        thematicBreak: true,
        alignment: AlignmentType.LEFT,
        style: "sectionsubheading",
        spacing: {
            before,
        },
        children: textFormatting(title, {bold: true,  allCaps: true})
    })
}

const createExperienceHeading = (leftText, rightText, {before=0}={before: 0}) => {
    const borders = {
        top: {
            size: 0,
            color: "#FFFFFF",
            style: BorderStyle.NONE,
        },
        bottom: {
            size: 0,
            color: "FFFFFF",
            style: BorderStyle.NONE,
        },
        left: {
            size: 0,
            color: "FFFFFF",
            style: BorderStyle.NONE,
        },
        right: {
            size: 0,
            color: "FFFFFF",
            style: BorderStyle.NONE,
        },
    }

    return new Table({
        columnWidths: [5200, 5200],
        width: {
            size: 100,
            type: WidthType.PERCENTAGE,
        },
        rows: [
            new TableRow({
                children: [
                    new TableCell({
                        borders: borders,
                        width: { size: 50, type: WidthType.PERCENTAGE },
                        children: [
                            new ParagraphDocx({
                                style: "subheading",
                                spacing: {
                                    before,
                                },
                                alignment: AlignmentType.LEFT,
                                children: textFormatting(leftText || '', {bold: true})
                            })
                        ],
                    }),
                    new TableCell({
                        borders,
                        width: { size: 50, type: WidthType.PERCENTAGE },
                        children: [
                            new ParagraphDocx({
                                style: "subheading",
                                spacing: {
                                    before,
                                },
                                alignment: AlignmentType.RIGHT,
                                children: textFormatting(rightText || '', {bold: true})
                            })
                        ],
                    }),
                ],
            }),
        ],
    });
}

const createBulletPoint = (bullet) => {
    return new ParagraphDocx({
        children: textFormatting(`• ${bullet}`),
        alignment: AlignmentType.LEFT,
        style: "subheading",
      })
}

const buildCustomSection = (section) => 
    (section.items || [])
        .map((item, index) => {
            const arr = []
            arr.push(
                createExperienceHeading(
                    item.title,
                    item.value,
                    { before: index === 0 ? 75 : 0, after: 75 }
                )
            );

            // is used for spacing after bullet points
            arr.push(new ParagraphDocx({}))

            return arr
        }).flat()
        
const buildEducationsSection = (section) => 
    (section.items || [])
        .map((education, index) => {
            const arr = []
            arr.push(
                createExperienceHeading(
                    education.heading,
                    education.location,
                    { before: index === 0 ? 75 : 0, after: 75 }
                )
            );
            arr.push(
                createExperienceHeading(
                    education.title,
                    education.date,
                    { after: 75 }
                )
            );

            // is used for spacing after bullet points
            arr.push(new ParagraphDocx({}))

            return arr
        }).flat()

const MARKS_MAP = {
    'underline': { underline: {} },
    'bold': { bold: true },
    'italic': { italics: true }
}

const createChildren = (children, result = [], recursive = false) => {
    const childrenCopy = [...children];
    const first = childrenCopy.shift();
    
    const items = []
    
    if(first == null) {
        return result
    }

    let props = {}
    
    if(first?.text != null) {
        props['text'] = `${first.text}`
    }

    if(first?.content != null && Array.isArray(first.content)) {
        props['children'] = createChildren(first.content, [], true)
    }
    
    if(first?.marks != null && Array.isArray(first.marks)) {
        first.marks.forEach(mark => {
            props = {
                ...props,
                ...MARKS_MAP[mark]
            }
        })
    }
    
    result.push(new TextRun(props))

    if(childrenCopy.length > 0){
        return createChildren(childrenCopy, result)
    }
    
    return result;
}

const getDOCXMarkupFrom = ( content ) => {
    const docItems = [];
    
    content.forEach((item) => {
        let itemProps = {}
        
        if(item?.type === 'listItem'){
            itemProps = {
                ...itemProps,
                alignment: AlignmentType.LEFT,
                style: "subheading",
                text: BPS_LEVEL[item?.level || 0]
            }
        }

        if(item?.type === 'text'){
            itemProps['text'] = `${itemProps?.text || ''} ${item.text}`
        }
        
        if(item?.content != null && Array.isArray(item?.content)){
            itemProps['children'] = createChildren(item?.content);
        }

        docItems.push(new ParagraphDocx(itemProps));
    })

    return docItems
}

const extractListItems = (content) => {
    const contentCopy = [...content];
    
    const result = contentCopy.map((item, index) => {
        const data = [];
        const itemContent = item?.content
        
        if(item.type === 'listItem'){
            const content = itemContent.filter(item => {
                if(item.type === 'listItem'){
                    data.push(extractListItems([item]).flat())
                    
                    return false
                }
                
                return true
            })
            
            return [
                {
                    ...item,
                    content: extractListItems(content)
                },
                ...data
            ].flat()
        }else{
            return item
        }
    })

    return result.flat();
}

const buildExperiencesSection = (section) => 
    (section?.items || [])
        .map((experience, index) => {
            const arr = []
            
        arr.push(
            createExperienceHeading(
                experience?.heading || '',
                experience?.location || '',
                { before: index === 0 ? 75 : 0 }
            )
        );

        if(Array.isArray(experience?.bullet_points)) {
            (experience?.bullet_points || []).forEach(bulletPoint => {
                arr.push(createBulletPoint(bulletPoint));
            });
        }else{
            const content = extractContentFrom(experience?.bullet_points);
            const flatListItems = extractListItems(content);

            if(Array.isArray(experience?.points)) {
                (experience?.points || []).forEach(bulletPoint => {
                    arr.push(createBulletPoint(bulletPoint));
                });
            }else{
                const content = extractContentFrom(experience?.points || '');
                const flatListItems = extractListItems(content);

                return getDOCXMarkupFrom(flatListItems);
            }
        }

        return arr
    }).flat()
    

const parseSection = (section, index) => {
    let beforeSpacing = index === 0 ? 75 : 0

    switch (section.type) {
        case BuilderSections.Education:
          return [
            createSectionHeading(section.title, {before: beforeSpacing}),
            ...buildEducationsSection(section, index === 0)
          ]

        case BuilderSections.CustomExperience:
            return [
                createSectionHeading(section.title, {before: beforeSpacing}),
                ...buildCustomSection(section, index === 0)
              ]
    
        default:
            return [
                createSectionHeading(section.title, {before: beforeSpacing}),
                ...buildExperiencesSection(section)
            ]
    }
}

const textFormatting = (value, defaultValues = {}) => {
    let returnsEmpty = () => [new TextRun({text: ''})]

    // if no value return empty text
    if(!value || ! value.trim()) return returnsEmpty()
  
    let extensions = getEditorExtensions(true, true, 'true', undefined)
  
    let json = generateJSON(value, extensions).content[0].content
    // let json = generateJSON(value, extensions).content.map(({content}) => {
    //     if(! content) return
    //     return {text: content?.[0]?.text, marks: content?.[0]?.marks}
    //   }).filter(el => el)

    if(!json) return returnsEmpty()

    let formatted = json.map(({ text, marks = [] }) => {
      // formatting font family
      const font = marks.find(el => el.type == 'textStyle')?.attrs?.['fontFamily']?.split(',')[0]?.replaceAll('"', '') ?? null

      // for links
      let underline = marks.some(el => el.type == 'underline') ? {type: UnderlineType.SINGLE } : null
  
      // color
      let color = marks.find(el => el.type == 'textStyle')?.attrs?.['color'] ?? null
  
      // check if color is rgb than transform to hex
      if(color && color.match(/(rgb)+/g)) {
        let [r,g,b] = color.match(/\d+/gm)
        color = rgbToHex(parseInt(r),parseInt(g),parseInt(b))
      }

      const link = marks.find(el => el.type == 'link')?.attrs?.href ?? null
      if(link && ! underline) underline = {type: UnderlineType.DOUBLE, color: '#2A8176' }
      if(link) color = '#000000'
      
      // formatting
      const bold = marks.some(el => el.type == 'bold')
      const italics = marks.some(el => el.type == 'italic')
      
      let values = {
        ...{text, bold, italics, font, color, underline},
        ...defaultValues
      }

      // if text has link than return different object
      if(link) {
        return new ExternalHyperlink({
            children: [
                new TextRun(values),
            ],
            link: link,
        })
      } else {
        return new TextRun(values)
      }
    })
  
    return formatted
  }

export const downloadResume = (data, {
    generatorDefaultSize, generatorHeadingSize, generatorLineHeight, generatorMargins, generatorFontFamily, isCoverLetter = false
  }) => {

    const parser = new DOMParser();
    const parsed = parser.parseFromString(data?.message, 'text/html');
    const getElemNodes = (nodeList) => Array.from(nodeList).filter(node => [Node.ELEMENT_NODE].includes(node.nodeType));

    const doc = new DocumentDocx({
        creator: "Wonsulting",
        title: decodeHtmlEntities(data?.title),
        styles: {
            paragraphStyles: [
                {
                    id: "heading",
                    name: "Heading",
                    basedOn: "Normal",
                    next: "Normal",
                    run: {
                        size: generatorHeadingSize,
                        font: generatorFontFamily
                    },
                    paragraph: {
                        spacing: {
                            after: generatorLineHeight
                        },
                        alignment: AlignmentType.CENTER
                    },
                },
                {
                    id: "subheading",
                    name: "Subheading",
                    basedOn: "heading",
                    next: "Normal",
                    run: {
                        size: generatorDefaultSize,
                    },
                },
                {
                    id: "sectionsubheading",
                    name: "Sectionsubheading",
                    basedOn: "heading",
                    next: "Normal",
                    run: {
                        size: generatorDefaultSize,
                    },
                },
            ],
        },
        sections: [
            {
                properties: {
                    page: {
                        margin: {
                            top: generatorMargins.topMargin,
                            bottom: generatorMargins.bottomMargin,
                            right: generatorMargins.rightMargin,
                            left: generatorMargins.leftMargin,
                        },
                    },
                },
                children: [
                    createHeading(data?.name),
                    createSubheading(data?.contact, {thematicBreak: isCoverLetter}),

                    ...(isCoverLetter ? getElemNodes(parsed.body.childNodes).map(el => {
                        return createMessage(el?.outerHTML, {spacing: {
                            before: 200
                        }})
                    }) : [{}]),

                     ...(data?.sections || []).map(parseSection).flat()
                ],
            },
        ],
    });

    Packer.toBlob(doc).then((blob) => {
        saveAs(blob, (extractContent(data?.title) || 'Untitled') + ".docx");
    });
}
